/**
 * Product panel tab "Förderfähigkeit (BEG EM)": when this product was on a BAFA
 * list of eligible products, and with which values.
 *
 * The point of the tab is the TIME axis. Funding is bound to the date of
 * application, not to today, so "is it eligible now" is the least interesting
 * answer here — "was it eligible in March 2023" is the one that decides a case.
 * The server (GET /products/:id/bafa-eligibility) returns the listing intervals
 * derived from the published lists; this component renders them.
 *
 * Three things are deliberately visible rather than hidden:
 *  - the match is a scored CANDIDATE, not a merged fact (the crawler never
 *    auto-merges), so its confidence is shown;
 *  - one product can map to several BAFA configurations (storage variants are
 *    separate entries by design) — each is listed with its own timeline;
 *  - where BAFA and our other sources contradict each other on an attribute, the
 *    contradiction is shown instead of silently picking a winner.
 */

import "../App.sass";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { authenticatedFetch } from "../JWTAuthService";
import { configuration } from "../Configuration";
import LoadingIndicator from "../components/LoadingIndicator";
import Section from "../components/Section";
import EnergyClassLadder from "./EnergyClassLadder";

interface Properties {
  product: any;
}

interface Interval {
  listedFrom: string | null;
  listedUntil: string | null;
  open: boolean;
  snapshots: number | null;
  relisting: boolean;
}

interface Listing {
  entryKey: string;
  sourceId: string;
  manufacturer: string | null;
  model: string | null;
  bauart: string | null;
  currentlyListed: boolean;
  intervals: Interval[];
  events: any[];
  attributes: Record<string, any>;
  attributeHistory: any[];
  match: {
    confidence: number | null;
    matchClass: string | null;
    hasAttributeConflict: boolean;
    conflicts: any[];
    deviations: any[];
  };
}

const ATTRIBUTE_LABELS: Record<string, string> = {
  ratedHeatOutputKw: "Nennwärmeleistung",
  ratedHeatOutputKw35: "Nennwärmeleistung 35 °C",
  ratedHeatOutputKw55: "Nennwärmeleistung 55 °C",
  etas: "ETAs",
  etas35: "ETAs 35 °C",
  etas55: "ETAs 55 °C",
  refrigerant: "Kältemittel",
  refrigerantChargeKg: "Füllmenge",
  scop: "SCOP",
  compressorCount: "Anzahl Verdichter",
  gridReady: "Netzdienlich",
  eeDisplay: "EE-Anzeige",
  energyEfficiencyIndex: "Energieeffizienzindex",
  grossAreaM2: "Bruttofläche",
  collectorYieldKwhM2a: "Kollektorertrag",
  bafaAnlagennummer: "BAFA-Anlagennummer",
};

const ATTRIBUTE_UNITS: Record<string, string> = {
  ratedHeatOutputKw: "kW",
  ratedHeatOutputKw35: "kW",
  ratedHeatOutputKw55: "kW",
  etas: "%",
  etas35: "%",
  etas55: "%",
  refrigerantChargeKg: "kg",
  grossAreaM2: "m²",
  collectorYieldKwhM2a: "kWh/(m²·a)",
};

/** Values that are provenance, not measurements — shown elsewhere or not at all. */
const HIDDEN_ATTRIBUTES = new Set(["sectionRaw", "bafaUuid", "gtin", "articleNumber"]);

/**
 * EPREL performance values worth surfacing next to the class, in reading order.
 * The keys are the German property labels the EPREL importer writes, so they can
 * double as the card labels — no extra label table to keep in sync. Which of them
 * a product actually carries depends on its category; absent keys are skipped.
 */
const EPREL_KPI_KEYS = [
  "Jahreszeitbedingte Raumheizungs-Energieeffizienz (ηs)",
  "ηs Mitteltemperatur (warm)",
  "Nennwärmeleistung (kalt, 55°C)",
  "Nennwärmeleistung Mitteltemperatur",
  "Schallleistungspegel",
  "Wärmeerzeugertyp",
];

/** Identity/status fields shown as a compact meta line above the KPI cards. */
const EPREL_META_KEYS = ["EPREL-Registriernummer", "Status", "Veröffentlicht am"];

/** Enum codes EPREL stores verbatim; softened for display only. */
const EPREL_VALUE_LABELS: Record<string, string> = {
  HEAT_PUMP: "Wärmepumpe",
  PUBLISHED: "veröffentlicht",
};

/** EPREL serialises dates as [year, month(1–12), day]; render them dd.mm.yyyy. */
const formatEprelValue = (value: any): string => {
  if (Array.isArray(value) && value.length === 3) {
    const [y, m, d] = value;
    return `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`;
  }
  if (value == null || value === "") return "—";
  const text = String(value);
  return EPREL_VALUE_LABELS[text] || text;
};

/** Source-of-truth for the efficiency block's values is the EPREL product itself. */
const EprelFacts = (props: { properties: Record<string, any> }) => {
  const meta = EPREL_META_KEYS.filter(
    key => props.properties[key] != null && props.properties[key] !== "",
  );
  const kpis = EPREL_KPI_KEYS.filter(
    key => props.properties[key] != null && props.properties[key] !== "",
  );
  if (meta.length === 0 && kpis.length === 0) return null;

  return (
    <div className="marginTopM">
      {meta.length > 0 ? (
        <div style={{ fontSize: 12, color: "#6b7480", marginBottom: 8 }}>
          {meta.map((key, index) => (
            <span key={key}>
              {index > 0 ? " · " : ""}
              {key}: {formatEprelValue(props.properties[key])}
            </span>
          ))}
        </div>
      ) : null}
      {kpis.length > 0 ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {kpis.map(key => (
            <div
              key={key}
              style={{
                border: "1px solid #e7eaee",
                borderRadius: 4,
                padding: "4px 10px",
                minWidth: 150,
              }}
            >
              <div style={{ fontSize: 11, color: "#6b7480" }}>{key}</div>
              <div>{formatEprelValue(props.properties[key])}</div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const formatDate = (iso: string | null | undefined): string => {
  if (!iso) return "—";
  // strip time component if present (ISO datetime)
  const dateOnly = String(iso).substring(0, 10);
  const [y, m, d] = dateOnly.split("-");
  return y && m && d ? `${d}.${m}.${y}` : String(iso);
};

const formatValue = (key: string, value: any, t: (key: string) => string): string => {
  if (value === true) return t("global.yes");
  if (value === false) return t("global.no");
  if (value == null || value === "") return "—";
  const unit = ATTRIBUTE_UNITS[key];
  return unit ? `${value} ${unit}` : String(value);
};

const Badge = (properties: { text: string; tone: "ok" | "warn" | "muted" }) => {
  const tones = {
    ok: { background: "#e8f5ec", border: "#b6ddc2", color: "#1c5c31" },
    warn: { background: "#fdf1e3", border: "#e8c9a0", color: "#8a4b12" },
    muted: { background: "#f1f3f6", border: "#d5dae1", color: "#4a5560" },
  } as const;
  const tone = tones[properties.tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "1px 8px",
        borderRadius: 10,
        fontSize: 12,
        background: tone.background,
        border: `1px solid ${tone.border}`,
        color: tone.color,
      }}
    >
      {properties.text}
    </span>
  );
};

export default function ProductEligibilityPanel(properties: Properties) {
  const { t } = useTranslation();
  const productId = properties.product?._id ? String(properties.product._id) : null;

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!productId) {
      setData(null);
      setLoading(false);
      return;
    }
    setData(null);
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setFailed(false);
      try {
        const response = await authenticatedFetch(
          `${configuration.serverUrl}/products/${productId}/bafa-eligibility`,
          { method: "GET" },
        );
        if (!response.ok) throw new Error(String(response.status));
        const payload = await response.json();
        if (!cancelled) setData(payload);
      } catch {
        // a missing eligibility view must not break the product panel
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  if (!productId) {
    return (
      <div className="paddingM">
        {t("settingsPanel.productEligibilityPanel.saveProductFirst")}
      </div>
    );
  }

  // build efficiency section from local product data before checking fetch status
  const productProperties = properties.product?.properties || {};
  const energyClass =
    properties.product?.energyClass || productProperties["Energieeffizienzklasse"] || null;
  const waterHeatingClass = productProperties["Warmwasser-Energieeffizienzklasse"] || null;

  const efficiencySection = energyClass ? (
    <Section title={t("settingsPanel.productEligibilityPanel.efficiencyTitle")}>
      <div className="paddingM">
        <EnergyClassLadder energyClass={energyClass} secondaryClass={waterHeatingClass} />
        <EprelFacts properties={productProperties} />
      </div>
    </Section>
  ) : null;

  if (loading) return <LoadingIndicator loading={true} />;
  if (failed) {
    return (
      <div className="paddingM width1200">
        {efficiencySection}
        <div className={efficiencySection ? "marginTopM" : ""}>
          {t("settingsPanel.productEligibilityPanel.loadFailed")}
        </div>
      </div>
    );
  }

  const listings: Listing[] = data?.listings || [];

  if (listings.length === 0) {
    return (
      <div className="paddingM width1200">
        {efficiencySection}
        <div className={efficiencySection ? "marginTopM" : ""}>
          {t("settingsPanel.productEligibilityPanel.noListing")}
        </div>
        <div className="marginTopS" style={{ color: "#6b7480", fontSize: 12 }}>
          {t("settingsPanel.productEligibilityPanel.noListingHint")}
        </div>
      </div>
    );
  }

  return (
    <div className="paddingM width1200">
      {efficiencySection}
      <div className={`marginBottomM ${efficiencySection ? "marginTopM" : ""}`}>
        {data.currentlyListed ? (
          <Badge tone="ok" text={t("settingsPanel.productEligibilityPanel.currentlyListed")} />
        ) : (
          <Badge tone="warn" text={t("settingsPanel.productEligibilityPanel.notListedAnymore")} />
        )}
        <span className="marginLeftS" style={{ color: "#4a5560" }}>
          {t("settingsPanel.productEligibilityPanel.firstListed")}:{" "}
          {formatDate(data.firstListedFrom)}
          {data.lastListedUntil
            ? ` · ${t("settingsPanel.productEligibilityPanel.lastListed")}: ${formatDate(
                data.lastListedUntil,
              )}`
            : ""}
        </span>
      </div>

      {listings.map(listing => (
        <Section
          key={listing.entryKey}
          title={`${listing.model || "—"} · ${listing.bauart || "—"}`}
        >
          <div className="paddingM">
            <div className="marginBottomS" style={{ fontSize: 12, color: "#4a5560" }}>
              {listing.manufacturer}
              {listing.match?.confidence != null ? (
                <span className="marginLeftS">
                  <Badge
                    tone={listing.match.confidence >= 0.8 ? "ok" : "muted"}
                    text={`${t(
                      "settingsPanel.productEligibilityPanel.matchConfidence",
                    )} ${Math.round(listing.match.confidence * 100)} %`}
                  />
                </span>
              ) : null}
              {listing.match?.hasAttributeConflict ? (
                <span className="marginLeftS">
                  <Badge
                    tone="warn"
                    text={t("settingsPanel.productEligibilityPanel.attributeConflict")}
                  />
                </span>
              ) : null}
            </div>

            {/* the timeline: one row per interval, open-ended while listed */}
            <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 12 }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: 12, color: "#6b7480" }}>
                  <th style={{ padding: "4px 8px" }}>
                    {t("settingsPanel.productEligibilityPanel.listedFrom")}
                  </th>
                  <th style={{ padding: "4px 8px" }}>
                    {t("settingsPanel.productEligibilityPanel.listedUntil")}
                  </th>
                  <th style={{ padding: "4px 8px" }}>
                    {t("settingsPanel.productEligibilityPanel.snapshots")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(listing.intervals || []).map((interval, index) => (
                  <tr key={index} style={{ borderTop: "1px solid #e7eaee" }}>
                    <td style={{ padding: "4px 8px" }}>{formatDate(interval.listedFrom)}</td>
                    <td style={{ padding: "4px 8px" }}>
                      {interval.open ? (
                        <Badge
                          tone="ok"
                          text={t("settingsPanel.productEligibilityPanel.stillListed")}
                        />
                      ) : (
                        formatDate(interval.listedUntil)
                      )}
                    </td>
                    <td style={{ padding: "4px 8px", color: "#6b7480" }}>
                      {interval.snapshots ?? "—"}
                      {interval.relisting
                        ? ` · ${t("settingsPanel.productEligibilityPanel.relisted")}`
                        : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* the values BAFA publishes for this configuration */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {Object.entries(listing.attributes || {})
                .filter(([key, value]) => !HIDDEN_ATTRIBUTES.has(key) && value != null)
                .map(([key, value]) => (
                  <div
                    key={key}
                    style={{
                      border: "1px solid #e7eaee",
                      borderRadius: 4,
                      padding: "4px 10px",
                      minWidth: 150,
                    }}
                  >
                    <div style={{ fontSize: 11, color: "#6b7480" }}>
                      {ATTRIBUTE_LABELS[key] || key}
                    </div>
                    <div>{formatValue(key, value, t)}</div>
                  </div>
                ))}
            </div>

            {(listing.match?.conflicts ?? []).length > 0 ? (
              <div
                className="marginTopM"
                style={{
                  padding: 8,
                  background: "#fdf1e3",
                  border: "1px solid #e8c9a0",
                  borderRadius: 4,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {t("settingsPanel.productEligibilityPanel.conflictsTitle")}
                </div>
                {(listing.match?.conflicts ?? []).map((conflict, index) => (
                  <div key={index} style={{ fontSize: 12 }}>
                    {ATTRIBUTE_LABELS[conflict.field] || conflict.field}: BAFA{" "}
                    {String(conflict.bafa)} · {conflict.against?.toUpperCase?.() || "EPREL"}{" "}
                    {String(conflict.other)}
                  </div>
                ))}
                <div className="marginTopS" style={{ fontSize: 11, color: "#8a4b12" }}>
                  {t("settingsPanel.productEligibilityPanel.conflictsHint")}
                </div>
              </div>
            ) : null}

            {/* de-listings are the signal we track deliberately */}
            {(listing.events || []).filter(e => e.type === "delisted").length > 0 ? (
              <div className="marginTopM" style={{ fontSize: 12, color: "#4a5560" }}>
                {(listing.events || [])
                  .filter(e => e.type === "delisted")
                  .map((event, index) => (
                    <div key={index}>
                      {t("settingsPanel.productEligibilityPanel.delistedOn", {
                        date: formatDate(event.effectiveDate),
                      })}
                    </div>
                  ))}
              </div>
            ) : null}
          </div>
        </Section>
      ))}

      <div className="marginTopM" style={{ fontSize: 11, color: "#6b7480" }}>
        {t("settingsPanel.productEligibilityPanel.evidenceHint")}
      </div>
    </div>
  );
}
